/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { snakeCase } from 'lodash';

import { Button, Chip, VisuallyHidden } from '@coursera/cds-core';
import type { ChipProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { FILTERS_CONFIG } from 'bundles/search-common/components/filters/getFiltersList';
import { SEARCH_FILTERS_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';

import _t from 'i18n!nls/enterprise-collections';

// @ts-expect-error TS2345: Argument of type 'ForwardRefExoticComponent<Omit<Props, "ref"> & RefAttributes<HTMLButtonElement>>' is not assignable to parameter of type 'ComponentType<Props>'.
const TrackedChip = withSingleTracked({ type: 'BUTTON' })<ChipProps>(Chip);

type Props = { currentFilters: string[]; removeFilters: (x0: string) => void; clearFilters?: () => void };

const styles = {
  filteredButtons: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,
};

export function CurrentAppliedFilters(props: Props) {
  const { currentFilters, removeFilters, clearFilters } = props;
  const [hasSelectedClearAll, setHasSelectedClearAll] = useState(false);

  const { getSearchResults } = useSearch();
  const searchResults = getSearchResults(SEARCH_FILTERS_INDEX_ID);
  const facets = searchResults?.facets;

  const clear = () => {
    setHasSelectedClearAll(true);
    clearFilters?.();
  };

  const ariaClearFiltersLabel = _t('All filter options have been cleared');
  const currentAriaLabelForFilters = hasSelectedClearAll ? ariaClearFiltersLabel : '';
  useEffect(() => {
    if (hasSelectedClearAll && currentFilters.length) setHasSelectedClearAll(false);
  }, [currentFilters, hasSelectedClearAll]);

  return (
    <div>
      {currentFilters.length > 0 && (
        <div>
          <div css={styles.filteredButtons}>
            {currentFilters.map((item) => {
              const [category, value] = item.split(':');
              const facet = facets?.find((f) => f.name === category);
              const facetValue = facet?.valuesAndCounts?.find((fv) => fv.value === value);
              const translatedCategoryName = FILTERS_CONFIG[category]?.getName();
              const itemDisplayValue = `${translatedCategoryName}: ${facetValue?.valueDisplay || value}`;
              return (
                <TrackedChip
                  key={itemDisplayValue}
                  trackingName={snakeCase(`filter_${category}_unchecked`)}
                  trackingData={{ label: itemDisplayValue }}
                  aria-label={_t('#{filterItem}, remove filter', { filterItem: itemDisplayValue })}
                  onClick={() => removeFilters(item)}
                  variant="delete"
                  role="button"
                >
                  {itemDisplayValue}
                </TrackedChip>
              );
            })}
          </div>
          <div style={{ marginLeft: '-8px' }}>
            <Button
              data-test="clear-all-button"
              trackingName="clear_all_filters_button"
              withVisibilityTracking={false}
              requireFullyVisible={false}
              component={TrackedButton}
              variant="ghost"
              onClick={clear}
              aria-label={_t('Clear all filters')}
            >
              {_t('Clear all')}
            </Button>
          </div>
        </div>
      )}
      <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
        <div role="alert" aria-live="polite">
          {currentAriaLabelForFilters}
        </div>
      </VisuallyHidden>
    </div>
  );
}

export default CurrentAppliedFilters;
